import { useEffect, useState } from "react";
import axios from "axios";
import { getStripe } from "@/services/stripe";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { useToast } from "@/hooks/use-toast";
import { usePaymentMethods } from "@/api/use-payment-methods-api";
import {
  useCreditDeposit,
  useRefreshCredit,
} from "@/api/use-payment-options-api";
import { CreditCard, Wallet } from "lucide-react";
import { PaymentMethodsError } from "./payment-methods-error";
import { useTranslation } from "react-i18next";

interface AddCreditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Sized to what prepay customers actually deposit ($10–$500).
const PRESET_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000];
// Server-enforced caps (ezx-server account-credit constants).
const DEPOSIT_MAX_CENTS = 50_000;

function formatCurrency(cents: number) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function AddCreditDialog({ open, onOpenChange }: AddCreditDialogProps) {
  const { t } = useTranslation("app");
  const { toast } = useToast();
  const {
    data: billingData,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = usePaymentMethods();
  const depositMutation = useCreditDeposit();
  const refreshCredit = useRefreshCredit();

  const [selectedAmount, setSelectedAmount] = useState<number | "custom">(5000);
  const [customAmount, setCustomAmount] = useState("");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  const savedCards = billingData?.paymentMethods ?? [];
  const defaultCard = savedCards.find((c) => c.isDefault) ?? savedCards[0];

  useEffect(() => {
    if (defaultCard && selectedPaymentMethodId === null && !isError) {
      setSelectedPaymentMethodId(defaultCard.id);
    }
  }, [defaultCard, selectedPaymentMethodId, isError]);

  const selectedCard = savedCards.find(
    (method) => method.id === selectedPaymentMethodId
  );

  const getAmountCents = (): number => {
    if (selectedAmount === "custom") {
      const parsed = parseFloat(customAmount);
      return isNaN(parsed) ? 0 : Math.round(parsed * 100);
    }
    return selectedAmount;
  };

  const [isConfirming, setIsConfirming] = useState(false);

  const handleSubmit = async () => {
    const amountCents = getAmountCents();
    if (amountCents < 100) {
      toast({ variant: "destructive", title: t("addCredit.minAmount") });
      return;
    }
    if (amountCents > DEPOSIT_MAX_CENTS) {
      toast({
        variant: "destructive",
        title: t("addCredit.maxDeposit", {
          amount: formatCurrency(DEPOSIT_MAX_CENTS),
        }),
      });
      return;
    }
    if (!selectedCard || isLoading || isError) {
      toast({
        variant: "destructive",
        title: t("addCredit.selectPaymentMethod"),
      });
      return;
    }
    try {
      setIsConfirming(true);
      const stripe = await getStripe();
      // Server returns an UNCONFIRMED PaymentIntent — confirming here lets
      // stripe.js run any 3DS challenge. The deposit is booked server-side
      // by the payment_intent.succeeded webhook.
      const { clientSecret } = await depositMutation.mutateAsync({
        amountCents,
        paymentMethodId: selectedCard.id,
      });

      const result = await stripe.confirmCardPayment(clientSecret);
      if (result.error) {
        toast({
          variant: "destructive",
          title: t("addCredit.cardDeclined"),
          description: result.error.message ?? t("addCredit.tryAnotherCard"),
        });
        return;
      }

      // The webhook books the ledger row after confirmation — refresh on a
      // short backoff so the Settings balance catches it.
      refreshCredit();
      toast({
        title: t("addCredit.depositReceived"),
        description: t("addCredit.depositDescription", {
          amount: formatCurrency(amountCents),
        }),
      });
      onOpenChange(false);
      setSelectedAmount(5000);
      setCustomAmount("");
    } catch (error: unknown) {
      const data = axios.isAxiosError<{ error?: string; message?: string }>(
        error
      )
        ? error.response?.data
        : undefined;
      toast({
        variant: "destructive",
        title: t("addCredit.failedTitle"),
        description:
          (data?.error === "CARD_DECLINED" ? data.message : data?.error) ??
          t("addCredit.tryAgain"),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const amountCents = getAmountCents();
  const isValid =
    amountCents >= 100 &&
    amountCents <= DEPOSIT_MAX_CENTS &&
    !!selectedCard &&
    !isLoading &&
    !isError;
  const isPending = depositMutation.isPending || isConfirming;

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="md"
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[calc(100dvh-2rem)]",
        header: "shrink-0",
        footer: "shrink-0",
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                <span>{t("addCredit.title")}</span>
              </div>
              <span className="text-small font-normal text-default-500">
                {t("addCredit.description")}
              </span>
            </ModalHeader>
            <ModalBody className="gap-6">
              {/* Amount Selection */}
              <div className="space-y-3">
                <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                  {t("addCredit.selectAmount")}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {PRESET_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      color={selectedAmount === amount ? "primary" : "default"}
                      variant={selectedAmount === amount ? "solid" : "bordered"}
                      onPress={() => setSelectedAmount(amount)}
                      className="h-12"
                    >
                      {formatCurrency(amount)}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    color={selectedAmount === "custom" ? "primary" : "default"}
                    variant={selectedAmount === "custom" ? "solid" : "bordered"}
                    onPress={() => setSelectedAmount("custom")}
                  >
                    {t("actions.custom")}
                  </Button>
                  {selectedAmount === "custom" && (
                    <Input
                      type="number"
                      min="1"
                      max={DEPOSIT_MAX_CENTS / 100}
                      step="0.01"
                      placeholder="0.00"
                      value={customAmount}
                      onValueChange={setCustomAmount}
                      startContent={
                        <span className="text-default-400 text-small">$</span>
                      }
                      variant="flat"
                      className="flex-1"
                    />
                  )}
                </div>
                <p className="text-tiny text-default-400">
                  {t("addCredit.maxBody", {
                    max: formatCurrency(DEPOSIT_MAX_CENTS),
                    daily: formatCurrency(DEPOSIT_MAX_CENTS * 2),
                  })}
                </p>
              </div>

              {/* Payment Method Selection */}
              <div className="space-y-3">
                <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                  {t("addCredit.paymentMethod")}
                </div>
                {isLoading ? (
                  <Spinner size="sm" />
                ) : isError ? (
                  <PaymentMethodsError
                    onRetry={() => void refetch()}
                    isRetrying={isFetching}
                  />
                ) : savedCards.length === 0 ? (
                  <p className="text-small text-default-500">
                    {t("addCredit.addPaymentMethodFirst")}
                  </p>
                ) : (
                  <Select
                    aria-label={t("addCredit.paymentMethod")}
                    placeholder={t("addCredit.selectPaymentMethod")}
                    selectedKeys={selectedCard ? [selectedCard.id] : []}
                    disallowEmptySelection
                    isDisabled={isPending}
                    maxListboxHeight={240}
                    classNames={{ value: "truncate" }}
                    startContent={<CreditCard className="h-4 w-4 shrink-0" />}
                    onSelectionChange={(keys) => {
                      if (keys === "all") return;
                      const id = Array.from(keys)[0];
                      if (typeof id === "string")
                        setSelectedPaymentMethodId(id);
                    }}
                  >
                    {savedCards.map((method, index) => {
                      const label = [
                        t("pickup.common.cardEnding", {
                          brand: method.brand ?? t("pickup.common.card"),
                          last4: method.last4,
                        }),
                        t("settings.expiresShort", {
                          month: method.expiryMonth,
                          year: method.expiryYear,
                        }),
                        method.isDefault ? t("settings.default") : null,
                        savedCards.some(
                          (other) =>
                            other.id !== method.id &&
                            other.brand === method.brand &&
                            other.last4 === method.last4 &&
                            other.expiryMonth === method.expiryMonth &&
                            other.expiryYear === method.expiryYear
                        )
                          ? t("pickup.confirm.savedCardNumber", {
                              number: index + 1,
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <SelectItem key={method.id} textValue={label}>
                          <span className="break-words whitespace-normal">
                            {label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </Select>
                )}
              </div>

              {/* Summary */}
              {amountCents >= 100 && (
                <div className="rounded-lg bg-default-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-small text-default-500">
                      {t("addCredit.amountToAdd")}
                    </span>
                    <span className="text-lg font-bold">
                      {formatCurrency(amountCents)}
                    </span>
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} className="flex-1">
                {t("actions.cancel")}
              </Button>
              <Button
                onPress={handleSubmit}
                color="primary"
                className="flex-1"
                isDisabled={!isValid || isPending}
                isLoading={isPending}
                startContent={
                  !isPending ? <CreditCard className="h-4 w-4" /> : null
                }
              >
                {isPending
                  ? t("actions.processing")
                  : t("addCredit.addAmount", {
                      amount: formatCurrency(amountCents),
                    })}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
